import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadIcon, DownloadIcon, FileTextIcon, CheckIcon, XIcon, InfoIcon, AlertCircleIcon, ClockIcon, PlusIcon, FilterIcon, RefreshCwIcon, FileIcon, EyeIcon, ArrowRightIcon } from 'lucide-react';
import * as XLSX from 'xlsx';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
import { useAuth } from '../../context/AuthContext';
// Mock data for parent merchants dropdown
const parentMerchants = [{
  id: 'M001',
  name: 'ABC Corporation'
}, {
  id: 'M002',
  name: 'XYZ Enterprises'
}, {
  id: 'M003',
  name: 'Local Grocers'
}, {
  id: 'M004',
  name: 'Tech Solutions Inc'
}, {
  id: 'M005',
  name: 'Metro Retailers'
}];
// Mock data for MCC codes
const mccCodes = [{
  code: '5411',
  description: 'Grocery Stores, Supermarkets'
}, {
  code: '5812',
  description: 'Eating Places, Restaurants'
}, {
  code: '5912',
  description: 'Drug Stores, Pharmacies'
}, {
  code: '5942',
  description: 'Book Stores'
}, {
  code: '7011',
  description: 'Hotels, Motels, Resorts'
}, {
  code: '7299',
  description: 'Other Services'
}];
// Mock data for business types
const businessTypes = ['Individual', 'Sole proprietor', 'Partnership', 'Corporate', 'Public limited'];
// Mock data for batch requests
const mockBatchRequests = [{
  id: 'BR001',
  requestedBy: 'admin@example.com',
  requestedAt: '2023-10-28T09:30:00Z',
  merchantCount: 15,
  validCount: 12,
  errorCount: 3,
  status: 'pending',
  description: 'October Batch 1 - New Merchants'
}, {
  id: 'BR002',
  requestedBy: 'admin@example.com',
  requestedAt: '2023-10-25T14:45:00Z',
  merchantCount: 8,
  validCount: 8,
  errorCount: 0,
  status: 'approved',
  description: 'October Batch 2 - Restaurant Chain',
  approvedBy: 'checker@example.com',
  approvedAt: '2023-10-26T10:15:00Z'
}, {
  id: 'BR003',
  requestedBy: 'admin@example.com',
  requestedAt: '2023-10-20T11:20:00Z',
  merchantCount: 12,
  validCount: 9,
  errorCount: 3,
  status: 'rejected',
  description: 'October Batch 3 - Retail Stores',
  rejectedBy: 'checker@example.com',
  rejectedAt: '2023-10-21T09:30:00Z',
  rejectionReason: 'Missing documentation for 5 merchants'
}];
interface ErrorDetail {
  field: string;
  message: string;
}
interface Merchant {
  id: number;
  legalName: string;
  dba: string;
  cnicNtn: string;
  address: string;
  mobile: string;
  email: string;
  city: string;
  country: string;
  accountIban: string;
  bankName: string;
  bankCode: string;
  settlementMode: string;
  mcc: string;
  businessType: string;
  monthlyTxnCount: string;
  monthlyTxnValue: string;
  status: 'pending' | 'approved' | 'rejected' | 'error';
  mid: string;
  errors: ErrorDetail[];
}
interface BatchRequest {
  id: string;
  requestedBy: string;
  requestedAt: string;
  merchantCount: number;
  validCount: number;
  errorCount: number;
  status: 'pending' | 'approved' | 'rejected';
  description: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}
// Required fields for merchant validation
const requiredFields = ['legalName', 'dba', 'cnicNtn', 'address', 'mobile', 'email', 'city', 'country', 'accountIban', 'bankName', 'bankCode', 'mcc', 'businessType', 'monthlyTxnCount', 'monthlyTxnValue'];
// Field mappings from Excel to our data model
const fieldMappings: Record<string, string> = {
  LegalName: 'legalName',
  DBA: 'dba',
  'CNIC/NTN': 'cnicNtn',
  Address: 'address',
  Mobile: 'mobile',
  Email: 'email',
  City: 'city',
  Country: 'country',
  'Account/IBAN': 'accountIban',
  'Bank Name': 'bankName',
  'Bank Code': 'bankCode',
  'Settlement Mode': 'settlementMode',
  MCC: 'mcc',
  'Business Type': 'businessType',
  MonthlyTxnCount: 'monthlyTxnCount',
  MonthlyTxnValue: 'monthlyTxnValue'
};
const BulkMerchant: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    user
  } = useAuth() || {
    user: {
      role: 'maker'
    }
  }; // Default to maker if context is not available
  const [view, setView] = useState<'upload' | 'history' | 'checker'>('upload');
  const [selectedParentMerchant, setSelectedParentMerchant] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedMerchants, setUploadedMerchants] = useState<Merchant[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showErrorDetailModal, setShowErrorDetailModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [batchDescription, setBatchDescription] = useState('');
  const [batchRequests, setBatchRequests] = useState<BatchRequest[]>(mockBatchRequests);
  const [selectedBatchRequest, setSelectedBatchRequest] = useState<BatchRequest | null>(null);
  const [showBatchDetailModal, setShowBatchDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fileReadError, setFileReadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Add state to store submission stats for the success modal
  const [submissionStats, setSubmissionStats] = useState({
    totalCount: 0,
    validCount: 0,
    errorCount: 0
  });
  // Function to validate a merchant
  const validateMerchant = (merchant: Partial<Merchant>): ErrorDetail[] => {
    const errors: ErrorDetail[] = [];
    // Check required fields
    for (const field of requiredFields) {
      if (!merchant[field as keyof Merchant] || String(merchant[field as keyof Merchant]).trim() === '') {
        errors.push({
          field,
          message: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
        });
      }
    }
    // Validate email format
    if (merchant.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(merchant.email)) {
      errors.push({
        field: 'email',
        message: 'Invalid email format'
      });
    }
    // Validate IBAN format for Pakistan
    if (merchant.accountIban && (!merchant.accountIban.startsWith('PK') || merchant.accountIban.length !== 24)) {
      errors.push({
        field: 'accountIban',
        message: 'Invalid IBAN format - must start with PK followed by 22 characters'
      });
    }
    // Validate mobile number format for Pakistan
    if (merchant.mobile && !/^\+92\s\d{3}\s\d{7}$/.test(merchant.mobile)) {
      errors.push({
        field: 'mobile',
        message: 'Invalid mobile format - must be in format +92 3XX XXXXXXX'
      });
    }
    // Validate MCC code - must be a 4-digit numeric value
    if (merchant.mcc) {
      const mccString = String(merchant.mcc).trim();
      if (!/^\d{4}$/.test(mccString)) {
        errors.push({
          field: 'mcc',
          message: 'Invalid MCC code - must be a 4-digit numeric value'
        });
      }
    }
    // Check if business type is valid
    if (merchant.businessType && !businessTypes.includes(merchant.businessType)) {
      errors.push({
        field: 'businessType',
        message: 'Invalid business type'
      });
    }
    return errors;
  };
  // Function to read Excel file
  const readExcelFile = (file: File): Promise<Merchant[]> => {
    return new Promise((resolve, reject) => {
      setIsProcessing(true);
      setFileReadError(null);
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, {
            type: 'binary'
          });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          // Convert sheet to JSON
          const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet);
          if (jsonData.length === 0) {
            setFileReadError('No data found in the uploaded file');
            setIsProcessing(false);
            reject(new Error('No data found in the uploaded file'));
            return;
          }
          // Map Excel data to our merchant model
          const merchants: Merchant[] = jsonData.map((row, index) => {
            const merchant: Partial<Merchant> = {
              id: index + 1,
              status: 'pending',
              mid: '',
              errors: []
            };
            // Map fields from Excel to our model
            Object.keys(row).forEach(excelField => {
              const ourField = fieldMappings[excelField];
              if (ourField) {
                merchant[ourField as keyof Merchant] = row[excelField];
              }
            });
            // Validate the merchant
            const validationErrors = validateMerchant(merchant);
            // Set status based on validation
            if (validationErrors.length > 0) {
              merchant.status = 'error';
              merchant.errors = validationErrors;
            }
            return merchant as Merchant;
          });
          setIsProcessing(false);
          resolve(merchants);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setFileReadError('Error parsing the uploaded file. Please ensure it is a valid Excel file.');
          setIsProcessing(false);
          reject(error);
        }
      };
      reader.onerror = error => {
        console.error('Error reading file:', error);
        setFileReadError('Error reading the uploaded file');
        setIsProcessing(false);
        reject(error);
      };
      // Read the file as binary
      reader.readAsBinaryString(file);
    });
  };
  // Handle file selection
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadedFile(file);
      try {
        const merchants = await readExcelFile(file);
        setUploadedMerchants(merchants);
        setShowPreviewModal(true);
      } catch (error) {
        console.error('Failed to process file:', error);
        // Error is already set in the readExcelFile function
      }
    }
  };
  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Get validation summary
  const getValidationSummary = () => {
    const validCount = uploadedMerchants.filter(m => m.errors.length === 0).length;
    const errorCount = uploadedMerchants.filter(m => m.errors.length > 0).length;
    return {
      validCount,
      errorCount,
      totalCount: uploadedMerchants.length
    };
  };
  // Handle batch submission
  const handleSubmitBatch = () => {
    setIsSubmitting(true);
    // Filter out merchants with errors
    const validMerchants = uploadedMerchants.filter(m => m.errors.length === 0);
    const validationSummary = getValidationSummary();
    // Store the validation summary for the success modal
    setSubmissionStats({
      totalCount: validationSummary.totalCount,
      validCount: validationSummary.validCount,
      errorCount: validationSummary.errorCount
    });
    // Simulate API call
    setTimeout(() => {
      const newBatchId = `BR${String(batchRequests.length + 1).padStart(3, '0')}`;
      const newBatch: BatchRequest = {
        id: newBatchId,
        requestedBy: 'admin@example.com',
        requestedAt: new Date().toISOString(),
        merchantCount: validationSummary.totalCount,
        validCount: validationSummary.validCount,
        errorCount: validationSummary.errorCount,
        status: 'pending',
        description: batchDescription || `Batch Upload - ${new Date().toLocaleDateString()}`
      };
      setBatchRequests([newBatch, ...batchRequests]);
      setIsSubmitting(false);
      setShowSubmitModal(false);
      setShowSuccessModal(true);
      // Reset upload state
      setUploadedFile(null);
      setUploadedMerchants([]);
      setBatchDescription('');
    }, 1500);
  };
  // Handle batch approval
  const handleApproveBatch = () => {
    if (!selectedBatchRequest) return;
    const updatedBatches = batchRequests.map(batch => batch.id === selectedBatchRequest.id ? {
      ...batch,
      status: 'approved' as const,
      approvedBy: 'checker@example.com',
      approvedAt: new Date().toISOString()
    } : batch);
    setBatchRequests(updatedBatches);
    setShowApproveModal(false);
    setShowBatchDetailModal(false);
    setSelectedBatchRequest(null);
  };
  // Handle batch rejection
  const handleRejectBatch = () => {
    if (!selectedBatchRequest) return;
    const updatedBatches = batchRequests.map(batch => batch.id === selectedBatchRequest.id ? {
      ...batch,
      status: 'rejected' as const,
      rejectedBy: 'checker@example.com',
      rejectedAt: new Date().toISOString(),
      rejectionReason
    } : batch);
    setBatchRequests(updatedBatches);
    setShowRejectModal(false);
    setShowBatchDetailModal(false);
    setSelectedBatchRequest(null);
    setRejectionReason('');
  };
  // Handle export all merchants with status
  const handleExportAll = () => {
    if (uploadedMerchants.length === 0) return;
    // Create a workbook with the merchant data
    const wb = XLSX.utils.book_new();
    // Prepare data for export with error details
    const exportData = uploadedMerchants.map(merchant => {
      const exportRow: Record<string, any> = {
        'Legal Name': merchant.legalName,
        DBA: merchant.dba,
        'CNIC/NTN': merchant.cnicNtn,
        Address: merchant.address,
        Mobile: merchant.mobile,
        Email: merchant.email,
        City: merchant.city,
        Country: merchant.country,
        'Account/IBAN': merchant.accountIban,
        'Bank Name': merchant.bankName,
        'Bank Code': merchant.bankCode,
        'Settlement Mode': merchant.settlementMode,
        MCC: merchant.mcc,
        'Business Type': merchant.businessType,
        'Monthly Txn Count': merchant.monthlyTxnCount,
        'Monthly Txn Value': merchant.monthlyTxnValue,
        Status: merchant.status,
        'Validation Status': merchant.errors.length > 0 ? 'Failed' : 'Valid',
        'Error Count': merchant.errors.length
      };
      // Add error details if any
      if (merchant.errors.length > 0) {
        exportRow['Error Details'] = merchant.errors.map(e => `${e.field}: ${e.message}`).join('; ');
      }
      return exportRow;
    });
    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(exportData);
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Merchants');
    // Generate filename
    const fileName = `Merchant_Batch_${new Date().toISOString().split('T')[0]}.xlsx`;
    // Write the workbook and trigger download
    XLSX.writeFile(wb, fileName);
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Get status badge based on status
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <StatusBadge status="success" text="Approved" data-id="element-1425" />;
      case 'rejected':
        return <StatusBadge status="danger" text="Rejected" data-id="element-1426" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-1427" />;
      case 'error':
        return <StatusBadge status="danger" text="Error" data-id="element-1428" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-1429" />;
    }
  };
  // Show error details for a merchant
  const showErrorDetails = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowErrorDetailModal(true);
  };
  // Generate template file for download
  const generateTemplateFile = () => {
    // Create a workbook with template structure
    const wb = XLSX.utils.book_new();
    // Define the column headers based on required fields
    const templateData = [{
      LegalName: '',
      DBA: '',
      'CNIC/NTN': '',
      Address: '',
      Mobile: '',
      Email: '',
      City: '',
      Country: '',
      'Account/IBAN': '',
      'Bank Name': '',
      'Bank Code': '',
      'Settlement Mode': '',
      MCC: '',
      'Business Type': '',
      MonthlyTxnCount: '',
      MonthlyTxnValue: ''
    }];
    // Create worksheet from template data
    const ws = XLSX.utils.json_to_sheet(templateData);
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    // Generate filename
    const fileName = 'Merchant_Upload_Template.xlsx';
    // Write the workbook and trigger download
    XLSX.writeFile(wb, fileName);
  };
  // Columns for the merchant preview table
  const merchantColumns = [{
    header: 'Legal Name',
    accessor: 'legalName',
    sortable: true
  }, {
    header: 'DBA',
    accessor: 'dba',
    sortable: true
  }, {
    header: 'CNIC/NTN',
    accessor: 'cnicNtn',
    sortable: true
  }, {
    header: 'Contact',
    accessor: (merchant: Merchant) => <div data-id="element-1430">
          <div data-id="element-1431">{merchant.mobile}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1432">
            {merchant.email}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Location',
    accessor: (merchant: Merchant) => <div data-id="element-1433">
          <div data-id="element-1434">{merchant.city}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1435">
            {merchant.country}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Business Type',
    accessor: 'businessType',
    sortable: true
  }, {
    header: 'MCC',
    accessor: 'mcc',
    sortable: true
  }, {
    header: 'Status',
    accessor: (merchant: Merchant) => <div data-id="element-1436">
          {getStatusBadge(merchant.status)}
          {merchant.errors.length > 0 && <div className="mt-1 text-xs text-red-500 cursor-pointer hover:underline" onClick={e => {
        e.stopPropagation();
        showErrorDetails(merchant);
      }} data-id="element-1437">
              {merchant.errors.length}{' '}
              {merchant.errors.length === 1 ? 'error' : 'errors'}
            </div>}
        </div>,
    sortable: false
  }];
  // Columns for the batch request history table
  const batchColumns = [{
    header: 'Batch ID',
    accessor: 'id',
    sortable: true
  }, {
    header: 'Description',
    accessor: 'description',
    sortable: true
  }, {
    header: 'Requested By',
    accessor: 'requestedBy',
    sortable: true
  }, {
    header: 'Requested At',
    accessor: (batch: BatchRequest) => formatDate(batch.requestedAt),
    sortable: true
  }, {
    header: 'Merchants',
    accessor: (batch: BatchRequest) => <div data-id="element-1438">
          <div data-id="element-1439">{batch.merchantCount} total</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1440">
            {batch.validCount} valid, {batch.errorCount} errors
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Status',
    accessor: (batch: BatchRequest) => getStatusBadge(batch.status),
    sortable: false
  }];
  // Columns for the checker inbox table
  const checkerColumns = [...batchColumns, {
    header: 'Action Date',
    accessor: (batch: BatchRequest) => batch.status === 'approved' ? formatDate(batch.approvedAt || '') : batch.status === 'rejected' ? formatDate(batch.rejectedAt || '') : 'Pending',
    sortable: true
  }];
  // Function to view batch details
  const viewBatchDetails = (batch: BatchRequest) => {
    setSelectedBatchRequest(batch);
    setShowBatchDetailModal(true);
  };
  // Render different views based on the current view state
  const renderView = () => {
    switch (view) {
      case 'upload':
        return <>
            <div className="mb-6" data-id="element-1441">
              <Card data-id="element-1442">
                <div className="mb-4" data-id="element-1443">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1444">
                    Upload Options
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1445">
                    <div data-id="element-1446">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1447">
                        Parent Merchant (Optional)
                      </label>
                      <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" value={selectedParentMerchant} onChange={e => setSelectedParentMerchant(e.target.value)} data-id="element-1448">
                        <option value="" data-id="element-1449">Create as Parent Merchants</option>
                        {parentMerchants.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-1450">
                            {merchant.name} ({merchant.id})
                          </option>)}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1451">
                        If selected, all uploaded merchants will be created as
                        sub-merchants under this parent merchant. If not
                        selected, all merchants will be created as parent
                        merchants.
                      </p>
                    </div>
                    <div data-id="element-1452">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1453">
                        Batch Description (Optional)
                      </label>
                      <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter batch description" value={batchDescription} onChange={e => setBatchDescription(e.target.value)} data-id="element-1454" />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1455">
                        Add a description to help identify this batch of
                        merchants.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-1456">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1457">
                    Upload Merchant Data
                  </h2>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6" data-id="element-1458">
                    <div className="text-center" data-id="element-1459">
                      <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-1460" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-1461">
                        Upload XLSX File
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1462">
                        File should include all required merchant information in
                        the correct format.
                      </p>
                      <div className="mt-4" data-id="element-1463">
                        <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} data-id="element-1464" />
                        <button type="button" onClick={handleUploadClick} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none" disabled={isProcessing} data-id="element-1465">
                          {isProcessing ? <>
                              <RefreshCwIcon className="animate-spin h-4 w-4 mr-2" data-id="element-1466" />
                              Processing...
                            </> : <>
                              <UploadIcon className="h-4 w-4 mr-2" data-id="element-1467" />
                              Select File
                            </>}
                        </button>
                      </div>
                      {fileReadError && <div className="mt-3 text-sm text-red-600 dark:text-red-400" data-id="element-1468">
                          <AlertCircleIcon className="inline-block h-4 w-4 mr-1" data-id="element-1469" />
                          {fileReadError}
                        </div>}
                    </div>
                  </div>
                  <div className="mt-4 flex items-start" data-id="element-1470">
                    <InfoIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-1471" />
                    <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1472">
                      <p className="font-medium" data-id="element-1473">Required File Format</p>
                      <p className="mt-1" data-id="element-1474">
                        Your XLSX file must include columns for: LegalName, DBA,
                        CNIC/NTN, Address, Mobile, Email, City, Country,
                        Account/IBAN, Bank Name, Bank Code, Settlement Mode,
                        MCC, Business Type, MonthlyTxnCount, and
                        MonthlyTxnValue.
                      </p>
                      <button onClick={generateTemplateFile} className="mt-2 inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline" data-id="element-1475">
                        <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1476" />
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            {uploadedMerchants.length > 0 && <Card data-id="element-1477">
                <div className="flex justify-between items-center mb-4" data-id="element-1478">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1479">
                    Preview Uploaded Merchants
                  </h2>
                  <div className="space-x-2" data-id="element-1480">
                    <button onClick={handleExportAll} className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1481">
                      <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1482" />
                      Export All
                    </button>
                    <button onClick={() => setUploadedMerchants([])} className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1483">
                      <XIcon className="h-4 w-4 mr-1" data-id="element-1484" />
                      Clear
                    </button>
                    <button onClick={() => setShowSubmitModal(true)} className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={uploadedMerchants.filter(m => m.errors.length === 0).length === 0} data-id="element-1485">
                      <CheckIcon className="h-4 w-4 mr-1" data-id="element-1486" />
                      Submit for Approval
                    </button>
                  </div>
                </div>
                {/* Validation Summary */}
                <div className="mb-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1487">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1488">
                    <div data-id="element-1489">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1490">
                        Validation Summary
                      </h3>
                      <div className="mt-1 flex items-center space-x-4" data-id="element-1491">
                        <div className="flex items-center" data-id="element-1492">
                          <div className="h-3 w-3 rounded-full bg-green-500 mr-1" data-id="element-1493"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1494">
                            {uploadedMerchants.filter(m => m.errors.length === 0).length}{' '}
                            Valid
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-1495">
                          <div className="h-3 w-3 rounded-full bg-red-500 mr-1" data-id="element-1496"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1497">
                            {uploadedMerchants.filter(m => m.errors.length > 0).length}{' '}
                            Failed
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-1498">
                          <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600 mr-1" data-id="element-1499"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1500">
                            {uploadedMerchants.length} Total
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 sm:mt-0" data-id="element-1501">
                      {uploadedMerchants.length > 10 && <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium" data-id="element-1502">
                          Large batch: {uploadedMerchants.length} merchants
                        </div>}
                    </div>
                  </div>
                </div>
                <div className="mb-3 flex items-center justify-between" data-id="element-1503">
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1504">
                    Showing {uploadedMerchants.length} merchants from{' '}
                    {uploadedFile?.name}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-1505">
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1506">
                      With errors:
                    </span>
                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs font-medium" data-id="element-1507">
                      {uploadedMerchants.filter(m => m.errors.length > 0).length}
                    </span>
                  </div>
                </div>
                <Table columns={merchantColumns} data={uploadedMerchants} keyField="id" actions={merchant => <div className="flex justify-end space-x-2" data-id="element-1509">
                      <button onClick={() => {
                // View merchant details
              }} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1510">
                        <FileTextIcon className="h-5 w-5" data-id="element-1511" />
                      </button>
                      {merchant.errors.length > 0 && <button onClick={() => showErrorDetails(merchant)} className="p-1 rounded-full text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="View Errors" data-id="element-1512">
                          <AlertCircleIcon className="h-5 w-5" data-id="element-1513" />
                        </button>}
                    </div>} data-id="element-1508" />
                <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1514">
                  <div className="flex" data-id="element-1515">
                    <InfoIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1516" />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1517">
                      <p className="font-medium" data-id="element-1518">Disclaimer</p>
                      <p className="mt-1" data-id="element-1519">
                        Only valid merchants will be forwarded for approval.
                        Records with validation errors (
                        {uploadedMerchants.filter(m => m.errors.length > 0).length}
                        ) will be dropped by the system during processing. You
                        can export the full list including errors for your
                        records.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>}
          </>;
      case 'history':
        return <Card data-id="element-1520">
            <div className="flex justify-between items-center mb-4" data-id="element-1521">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1522">
                Batch Upload History
              </h2>
              <div className="space-x-2" data-id="element-1523">
                <button onClick={() => setView('upload')} className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1524">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-1525" />
                  New Upload
                </button>
              </div>
            </div>
            <div className="mb-4 flex items-center space-x-2" data-id="element-1526">
              <div className="relative flex-grow max-w-xs" data-id="element-1527">
                <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-8 pr-3 text-sm" placeholder="Search batches..." data-id="element-1528" />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2" data-id="element-1529">
                  <FilterIcon className="h-4 w-4 text-gray-400" data-id="element-1530" />
                </div>
              </div>
              <select className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-3 pr-8 text-sm" data-id="element-1531">
                <option value="" data-id="element-1532">All Statuses</option>
                <option value="pending" data-id="element-1533">Pending</option>
                <option value="approved" data-id="element-1534">Approved</option>
                <option value="rejected" data-id="element-1535">Rejected</option>
              </select>
            </div>
            <Table columns={batchColumns} data={batchRequests} keyField="id" actions={batch => <div className="flex justify-end space-x-2" data-id="element-1537">
                  <button onClick={() => viewBatchDetails(batch)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1538">
                    <FileTextIcon className="h-5 w-5" data-id="element-1539" />
                  </button>
                  <button className="p-1 rounded-full text-green-500 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30" title="Download Results" data-id="element-1540">
                    <DownloadIcon className="h-5 w-5" data-id="element-1541" />
                  </button>
                </div>} data-id="element-1536" />
          </Card>;
      case 'checker':
        return <Card data-id="element-1542">
            <div className="flex justify-between items-center mb-4" data-id="element-1543">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1544">
                Merchant Batch Approval Queue
              </h2>
              <div className="space-x-2" data-id="element-1545">
                <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1546">
                  <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-1547" />
                  Refresh
                </button>
              </div>
            </div>
            <div className="mb-4 flex items-center justify-between" data-id="element-1548">
              <div className="flex items-center space-x-2" data-id="element-1549">
                <div className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs font-medium flex items-center" data-id="element-1550">
                  <ClockIcon className="h-3 w-3 mr-1" data-id="element-1551" />
                  <span data-id="element-1552">
                    {batchRequests.filter(b => b.status === 'pending').length}{' '}
                    Pending
                  </span>
                </div>
                <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-medium flex items-center" data-id="element-1553">
                  <CheckIcon className="h-3 w-3 mr-1" data-id="element-1554" />
                  <span data-id="element-1555">
                    {batchRequests.filter(b => b.status === 'approved').length}{' '}
                    Approved
                  </span>
                </div>
                <div className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs font-medium flex items-center" data-id="element-1556">
                  <XIcon className="h-3 w-3 mr-1" data-id="element-1557" />
                  <span data-id="element-1558">
                    {batchRequests.filter(b => b.status === 'rejected').length}{' '}
                    Rejected
                  </span>
                </div>
              </div>
              <div className="relative" data-id="element-1559">
                <select className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1.5 pl-3 pr-8 text-sm" data-id="element-1560">
                  <option value="" data-id="element-1561">All Batches</option>
                  <option value="pending" data-id="element-1562">Pending Only</option>
                </select>
              </div>
            </div>
            <Table columns={checkerColumns} data={batchRequests} keyField="id" actions={batch => <div className="flex justify-end space-x-2" data-id="element-1564">
                  <button onClick={() => viewBatchDetails(batch)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1565">
                    <FileTextIcon className="h-5 w-5" data-id="element-1566" />
                  </button>
                  {batch.status === 'pending' && <>
                      <button onClick={() => {
                setSelectedBatchRequest(batch);
                setShowApproveModal(true);
              }} className="p-1 rounded-full text-green-500 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30" title="Approve" data-id="element-1567">
                        <CheckIcon className="h-5 w-5" data-id="element-1568" />
                      </button>
                      <button onClick={() => {
                setSelectedBatchRequest(batch);
                setShowRejectModal(true);
              }} className="p-1 rounded-full text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Reject" data-id="element-1569">
                        <XIcon className="h-5 w-5" data-id="element-1570" />
                      </button>
                    </>}
                </div>} data-id="element-1563" />
          </Card>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-1571">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1572">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1573">
          Bulk Merchant Upload
        </h1>
        <div className="mt-3 sm:mt-0 flex space-x-2" data-id="element-1574">
          <button onClick={() => setView('upload')} className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'upload' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1575">
            Upload
          </button>
          <button onClick={() => setView('history')} className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'history' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1576">
            History
          </button>
          {user.role === 'checker' && <button onClick={() => setView('checker')} className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'checker' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1577">
              Checker Inbox
            </button>}
        </div>
      </div>
      {renderView()}
      {/* File Preview Modal */}
      <Modal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} title="File Preview" size="lg" data-id="element-1578">
        <div className="space-y-4" data-id="element-1579">
          <div className="flex items-center justify-between" data-id="element-1580">
            <div data-id="element-1581">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1582">
                {uploadedFile?.name || 'merchants.xlsx'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1583">
                {uploadedMerchants.length} merchants found in file
              </p>
            </div>
            <StatusBadge status={uploadedMerchants.some(m => m.errors.length > 0) ? 'warning' : 'success'} text={uploadedMerchants.some(m => m.errors.length > 0) ? 'Validation Issues' : 'Valid'} data-id="element-1584" />
          </div>
          {/* Validation Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1585">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1586">
              Validation Summary
            </h4>
            <div className="grid grid-cols-3 gap-2" data-id="element-1587">
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-center" data-id="element-1588">
                <div className="text-lg font-semibold text-green-700 dark:text-green-300" data-id="element-1589">
                  {uploadedMerchants.filter(m => m.errors.length === 0).length}
                </div>
                <div className="text-xs text-green-600 dark:text-green-400" data-id="element-1590">
                  Valid Records
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-center" data-id="element-1591">
                <div className="text-lg font-semibold text-red-700 dark:text-red-300" data-id="element-1592">
                  {uploadedMerchants.filter(m => m.errors.length > 0).length}
                </div>
                <div className="text-xs text-red-600 dark:text-red-400" data-id="element-1593">
                  Failed Records
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-center" data-id="element-1594">
                <div className="text-lg font-semibold text-blue-700 dark:text-blue-300" data-id="element-1595">
                  {uploadedMerchants.length}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400" data-id="element-1596">
                  Total Records
                </div>
              </div>
            </div>
            {uploadedMerchants.length > 10 && <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-2 text-sm text-yellow-700 dark:text-yellow-300 flex items-center" data-id="element-1597">
                <AlertCircleIcon className="h-4 w-4 mr-2 text-yellow-500" data-id="element-1598" />
                Large batch detected: {uploadedMerchants.length} merchants (more
                than 10)
              </div>}
          </div>
          {uploadedMerchants.some(m => m.errors.length > 0) && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1599">
              <div className="flex" data-id="element-1600">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1601" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1602">
                  <p className="font-medium" data-id="element-1603">
                    Some merchants have validation issues
                  </p>
                  <p className="mt-1" data-id="element-1604">
                    Only valid merchants will be processed. Records with errors
                    will be dropped during processing. You can export the full
                    list including errors for your records.
                  </p>
                </div>
              </div>
            </div>}
          <div className="max-h-96 overflow-y-auto" data-id="element-1605">
            <Table columns={merchantColumns} data={uploadedMerchants} keyField="id" pagination={false} data-id="element-1606" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1607">
            <button onClick={handleExportAll} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1608">
              <DownloadIcon className="h-4 w-4 mr-1 inline-block" data-id="element-1609" />
              Export All
            </button>
            <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1610">
              Close
            </button>
            <button onClick={() => {
            setShowPreviewModal(false);
            setShowSubmitModal(true);
          }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={uploadedMerchants.filter(m => m.errors.length === 0).length === 0} data-id="element-1611">
              Proceed to Submit
            </button>
          </div>
        </div>
      </Modal>
      {/* Error Detail Modal */}
      <Modal isOpen={showErrorDetailModal} onClose={() => setShowErrorDetailModal(false)} title="Validation Error Details" data-id="element-1612">
        {selectedMerchant && <div className="space-y-4" data-id="element-1613">
            <div className="flex items-center" data-id="element-1614">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 mr-3" data-id="element-1615">
                <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-1616" />
              </div>
              <div data-id="element-1617">
                <h3 className="text-lg font-medium" data-id="element-1618">
                  {selectedMerchant.legalName || selectedMerchant.dba}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1619">
                  {selectedMerchant.errors.length} validation{' '}
                  {selectedMerchant.errors.length === 1 ? 'error' : 'errors'}{' '}
                  found
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-1620">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1621">
                Error Details
              </h4>
              <div className="space-y-3" data-id="element-1622">
                {selectedMerchant.errors.map((error, index) => <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3" data-id="element-1623">
                    <div className="flex items-start" data-id="element-1624">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" data-id="element-1625" />
                      <div data-id="element-1626">
                        <div className="font-medium text-red-700 dark:text-red-300" data-id="element-1627">
                          {error.field}
                        </div>
                        <div className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-1628">
                          {error.message}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 mt-2" data-id="element-1629">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1630">
                Merchant Information
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-1631">
                <div data-id="element-1632">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1633">
                    Legal Name:
                  </span>{' '}
                  {selectedMerchant.legalName || '—'}
                </div>
                <div data-id="element-1634">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1635">DBA:</span>{' '}
                  {selectedMerchant.dba || '—'}
                </div>
                <div data-id="element-1636">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1637">
                    CNIC/NTN:
                  </span>{' '}
                  {selectedMerchant.cnicNtn || '—'}
                </div>
                <div data-id="element-1638">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1639">
                    Email:
                  </span>{' '}
                  {selectedMerchant.email || '—'}
                </div>
                <div data-id="element-1640">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1641">
                    Mobile:
                  </span>{' '}
                  {selectedMerchant.mobile || '—'}
                </div>
                <div data-id="element-1642">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1643">
                    IBAN:
                  </span>{' '}
                  {selectedMerchant.accountIban || '—'}
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1644">
              <div className="flex justify-end" data-id="element-1645">
                <button onClick={() => setShowErrorDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1646">
                  Close
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Submit Confirmation Modal */}
      <Modal isOpen={showSubmitModal} onClose={() => setShowSubmitModal(false)} title="Submit for Approval" data-id="element-1647">
        <div className="space-y-4" data-id="element-1648">
          <p className="text-gray-700 dark:text-gray-300" data-id="element-1649">
            You are about to submit a batch of merchants for approval.
            {selectedParentMerchant ? ` They will be created as sub-merchants under ${parentMerchants.find(m => m.id === selectedParentMerchant)?.name}.` : ' They will be created as parent merchants.'}
          </p>
          {/* Submission Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1650">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1651">
              Submission Summary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1652">
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-center" data-id="element-1653">
                <div className="text-lg font-semibold text-green-700 dark:text-green-300" data-id="element-1654">
                  {uploadedMerchants.filter(m => m.errors.length === 0).length}
                </div>
                <div className="text-xs text-green-600 dark:text-green-400" data-id="element-1655">
                  Valid Records
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-center" data-id="element-1656">
                <div className="text-lg font-semibold text-red-700 dark:text-red-300" data-id="element-1657">
                  {uploadedMerchants.filter(m => m.errors.length > 0).length}
                </div>
                <div className="text-xs text-red-600 dark:text-red-400" data-id="element-1658">
                  Failed Records
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-center" data-id="element-1659">
                <div className="text-lg font-semibold text-blue-700 dark:text-blue-300" data-id="element-1660">
                  {uploadedMerchants.length}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400" data-id="element-1661">
                  Total Records
                </div>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1662">
            <div className="flex" data-id="element-1663">
              <AlertCircleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" data-id="element-1664" />
              <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1665">
                <p className="font-medium" data-id="element-1666">Important Notice</p>
                <p className="mt-1" data-id="element-1667">
                  Only valid merchants (
                  {uploadedMerchants.filter(m => m.errors.length === 0).length}
                  ) will be submitted for approval. Records with validation
                  errors (
                  {uploadedMerchants.filter(m => m.errors.length > 0).length})
                  will be dropped during processing.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3" data-id="element-1668">
            <div className="flex" data-id="element-1669">
              <InfoIcon className="h-5 w-5 text-blue-400 mr-2" data-id="element-1670" />
              <div className="text-sm text-blue-700 dark:text-blue-300" data-id="element-1671">
                <p className="font-medium" data-id="element-1672">Approval Process</p>
                <p className="mt-1" data-id="element-1673">
                  Your submission will be reviewed by a checker. Once approved,
                  Merchant IDs (MIDs) will be generated and you'll be able to
                  download the results.
                </p>
              </div>
            </div>
          </div>
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1674">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1675">
              Batch Description
            </label>
            <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter a description for this batch" value={batchDescription} onChange={e => setBatchDescription(e.target.value)} data-id="element-1676" />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1677">
              Adding a clear description helps reviewers understand the purpose
              of this merchant batch.
            </p>
          </div>
          <div className="flex justify-end space-x-3 pt-4 mt-4" data-id="element-1678">
            <button onClick={() => setShowSubmitModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" disabled={isSubmitting} data-id="element-1679">
              Cancel
            </button>
            <button onClick={handleSubmitBatch} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center" disabled={isSubmitting} data-id="element-1680">
              {isSubmitting ? <>
                  <RefreshCwIcon className="animate-spin h-4 w-4 mr-2" data-id="element-1681" />
                  Submitting...
                </> : <>
                  <CheckIcon className="h-4 w-4 mr-2" data-id="element-1682" />
                  Submit for Approval
                </>}
            </button>
          </div>
        </div>
      </Modal>
      {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title="Submission Successful" data-id="element-1683">
        <div className="text-center py-4" data-id="element-1684">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30" data-id="element-1685">
            <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-1686" />
          </div>
          <h3 className="mt-3 text-lg font-medium text-gray-900 dark:text-white" data-id="element-1687">
            Merchants Submitted Successfully
          </h3>
          <div className="mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-left" data-id="element-1688">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1689">
              Submission Summary
            </h4>
            <div className="flex items-center justify-between mb-2" data-id="element-1690">
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1691">
                Total Records:
              </span>
              <span className="text-sm font-medium" data-id="element-1692">
                {submissionStats.totalCount}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2" data-id="element-1693">
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1694">
                Successfully Submitted:
              </span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-1695">
                {submissionStats.validCount}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-1696">
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1697">
                Failed Records:
              </span>
              <span className="text-sm font-medium text-red-600 dark:text-red-400" data-id="element-1698">
                {submissionStats.errorCount}
              </span>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-id="element-1699">
            Your batch has been submitted for approval. You'll be notified once
            it's approved.
          </p>
          <div className="mt-4 flex justify-center space-x-3" data-id="element-1700">
            <button onClick={() => {
            setShowSuccessModal(false);
            setView('history');
          }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1701">
              View Submission History
            </button>
            <button onClick={handleExportAll} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1702">
              <DownloadIcon className="h-4 w-4 mr-1 inline-block" data-id="element-1703" />
              Export Results
            </button>
          </div>
        </div>
      </Modal>
      {/* Batch Detail Modal */}
      <Modal isOpen={showBatchDetailModal} onClose={() => setShowBatchDetailModal(false)} title="Batch Request Details" size="lg" data-id="element-1704">
        {selectedBatchRequest && <div className="space-y-4" data-id="element-1705">
            <div className="flex items-center justify-between" data-id="element-1706">
              <div data-id="element-1707">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1708">
                  {selectedBatchRequest.description}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1709">
                  Batch ID: {selectedBatchRequest.id}
                </p>
              </div>
              {getStatusBadge(selectedBatchRequest.status)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1710">
              <div data-id="element-1711">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1712">
                  Requested By
                </p>
                <p className="font-medium" data-id="element-1713">
                  {selectedBatchRequest.requestedBy}
                </p>
              </div>
              <div data-id="element-1714">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1715">
                  Requested At
                </p>
                <p className="font-medium" data-id="element-1716">
                  {formatDate(selectedBatchRequest.requestedAt)}
                </p>
              </div>
              <div data-id="element-1717">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1718">
                  Merchant Count
                </p>
                <p className="font-medium" data-id="element-1719">
                  {selectedBatchRequest.merchantCount} total
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2" data-id="element-1720">
                    ({selectedBatchRequest.validCount} valid,{' '}
                    {selectedBatchRequest.errorCount} errors)
                  </span>
                </p>
              </div>
              <div data-id="element-1721">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1722">
                  Status
                </p>
                <p className="font-medium" data-id="element-1723">
                  {selectedBatchRequest.status.charAt(0).toUpperCase() + selectedBatchRequest.status.slice(1)}
                </p>
              </div>
              {selectedBatchRequest.status === 'approved' && <>
                  <div data-id="element-1724">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1725">
                      Approved By
                    </p>
                    <p className="font-medium" data-id="element-1726">
                      {selectedBatchRequest.approvedBy}
                    </p>
                  </div>
                  <div data-id="element-1727">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1728">
                      Approved At
                    </p>
                    <p className="font-medium" data-id="element-1729">
                      {selectedBatchRequest.approvedAt ? formatDate(selectedBatchRequest.approvedAt) : '-'}
                    </p>
                  </div>
                </>}
              {selectedBatchRequest.status === 'rejected' && <>
                  <div data-id="element-1730">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1731">
                      Rejected By
                    </p>
                    <p className="font-medium" data-id="element-1732">
                      {selectedBatchRequest.rejectedBy}
                    </p>
                  </div>
                  <div data-id="element-1733">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1734">
                      Rejected At
                    </p>
                    <p className="font-medium" data-id="element-1735">
                      {selectedBatchRequest.rejectedAt ? formatDate(selectedBatchRequest.rejectedAt) : '-'}
                    </p>
                  </div>
                  <div className="md:col-span-2" data-id="element-1736">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1737">
                      Rejection Reason
                    </p>
                    <p className="font-medium" data-id="element-1738">
                      {selectedBatchRequest.rejectionReason || '-'}
                    </p>
                  </div>
                </>}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-1739">
              <div className="flex justify-between items-center mb-3" data-id="element-1740">
                <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-1741">
                  Merchants in Batch
                </h4>
                <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1742">
                  <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1743" />
                  Export List
                </button>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-3" data-id="element-1744">
                Merchant data is not available for viewing in this demo.
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1745">
              {selectedBatchRequest.status === 'approved' && <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center" data-id="element-1746">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1747" />
                  Download Results
                </button>}
              {selectedBatchRequest.status === 'pending' && view === 'checker' && <>
                    <button onClick={() => {
              setShowBatchDetailModal(false);
              setShowRejectModal(true);
            }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/10" data-id="element-1748">
                      Reject
                    </button>
                    <button onClick={() => {
              setShowBatchDetailModal(false);
              setShowApproveModal(true);
            }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1749">
                      Approve
                    </button>
                  </>}
              <button onClick={() => setShowBatchDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1750">
                Close
              </button>
            </div>
          </div>}
      </Modal>
      {/* Approve Modal */}
      <Modal isOpen={showApproveModal} onClose={() => setShowApproveModal(false)} title="Approve Merchant Batch" footer={<>
            <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1752">
              Cancel
            </button>
            <button onClick={handleApproveBatch} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-1753">
              Approve Batch
            </button>
          </>} data-id="element-1751">
        {selectedBatchRequest && <div className="space-y-4" data-id="element-1754">
            <p className="text-gray-700 dark:text-gray-300" data-id="element-1755">
              You are about to approve batch{' '}
              <strong data-id="element-1756">{selectedBatchRequest.id}</strong> containing{' '}
              <strong data-id="element-1757">{selectedBatchRequest.validCount}</strong> valid
              merchants.
            </p>
            <p className="text-gray-700 dark:text-gray-300" data-id="element-1758">
              This will generate Merchant IDs (MIDs) for all valid merchants in
              the batch and make them available in the system.
            </p>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3" data-id="element-1759">
              <div className="flex" data-id="element-1760">
                <CheckIcon className="h-5 w-5 text-green-400 mr-2" data-id="element-1761" />
                <div className="text-sm text-green-700 dark:text-green-300" data-id="element-1762">
                  <p data-id="element-1763">
                    Merchant IDs will be automatically generated according to
                    the system's rules.
                  </p>
                </div>
              </div>
            </div>
            {selectedBatchRequest.errorCount > 0 && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1764">
                <div className="flex" data-id="element-1765">
                  <InfoIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1766" />
                  <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1767">
                    <p data-id="element-1768">
                      Note: {selectedBatchRequest.errorCount} merchants with
                      validation errors were dropped during processing and will
                      not be included in this approval.
                    </p>
                  </div>
                </div>
              </div>}
          </div>}
      </Modal>
      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Reject Merchant Batch" footer={<>
            <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1770">
              Cancel
            </button>
            <button onClick={handleRejectBatch} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" disabled={!rejectionReason.trim()} data-id="element-1771">
              Reject Batch
            </button>
          </>} data-id="element-1769">
        {selectedBatchRequest && <div className="space-y-4" data-id="element-1772">
            <p className="text-gray-700 dark:text-gray-300" data-id="element-1773">
              You are about to reject batch{' '}
              <strong data-id="element-1774">{selectedBatchRequest.id}</strong> containing{' '}
              <strong data-id="element-1775">{selectedBatchRequest.validCount}</strong> valid
              merchants.
            </p>
            <div data-id="element-1776">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1777">
                Rejection Reason <span className="text-red-500" data-id="element-1778">*</span>
              </label>
              <textarea rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Please provide a reason for rejection" value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} required data-id="element-1779" />
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1780">
              <div className="flex" data-id="element-1781">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1782" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1783">
                  <p data-id="element-1784">
                    The maker will need to create a new batch to address the
                    issues you've identified.
                  </p>
                </div>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default BulkMerchant;